import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import { Lock, LockOpen, Logout, Translate, UnfoldLess } from '@mui/icons-material'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import MenuIcon from '@mui/icons-material/Menu'
import {
  Box,
  ButtonBase,
  CssBaseline,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material'
import MuiAppBar from '@mui/material/AppBar'
import MuiDrawer from '@mui/material/Drawer'
import { styled } from '@mui/material/styles'

import { clearAppCaches } from 'utils/auxiliaries/delete-cache'

import { changeLanguage, stopUserImpersonate } from 'services/api'

import usePermission from '../../hooks/useHasPermission'
import { MENU_ITEMS } from './routes'

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { useUser } from 'UserContext'
import axios from 'axios'

const drawerWidth = 240

const openedMixin = theme => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
})

const closedMixin = theme => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
})

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}))

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: prop => prop !== 'open',
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}))

const Drawer = styled(MuiDrawer, { shouldForwardProp: prop => prop !== 'open' })(
  ({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  })
)

const MiniDrawer = () => {
  const { user } = useUser()
  const { t, i18n } = useTranslation('layout')
  const location = useLocation()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [openLock, setOpenLock] = useState(false)
  const [openSubmenus, setOpenSubmenus] = useState({})
  const [langAnchorEl, setLangAnchorEl] = useState(null)
  const hasPermission = usePermission()
  const queryClient = useQueryClient()
  const isDrawerOpen = open || openLock

  const changeLanguageSelected = () => {
    const lng = i18n.language === 'en' ? 'es' : 'en'
    i18n.changeLanguage(lng)
  }

  const handleOpenLangMenu = event => {
    setLangAnchorEl(event.currentTarget)
  }

  const handleCloseLangMenu = () => {
    setLangAnchorEl(null)
  }

  const mutate = useMutation({
    mutationFn: async lang => {
      return await changeLanguage(lang)
    },
    onSuccess: success => {
      i18n.changeLanguage(success?.data?.lang_preferred)
      handleCloseLangMenu()
    },
    onError: error => {
      toast.error(error.message)
    },
  })

  const handleChangeLanguage = lng => {
    mutate.mutate(lng)
  }
  const visibleItems = useMemo(() => {
    return MENU_ITEMS.filter(item => hasPermission(item.permissionTags)).map(item => {
      const visibleSubmenus = (item.submenus || [])
        .filter(sub => hasPermission(sub.permissionTags))
        .map(sub => {
          const visibleNested = (sub.submenus || []).filter(nested =>
            hasPermission(nested.permissionTags)
          )
          return { ...sub, submenus: visibleNested }
        })
      return { ...item, submenus: visibleSubmenus }
    })
  }, [hasPermission])

  useEffect(() => {
    const newState = {}
    visibleItems.forEach(item => {
      if (item.submenus?.some(sub => location.pathname.startsWith(sub.url))) {
        newState[item.id] = true
      }
    })
    setOpenSubmenus(prev => ({ ...prev, ...newState }))
  }, [location.pathname])

  const toggleSubmenu = id => {
    setOpenSubmenus(prev => ({ ...prev, [id]: !prev[id] }))
  }

  const logout = async () => {
    await axios
      .post(`${process.env.REACT_APP_URL_API}logout`, {}, { withCredentials: true })
      .then(() => {
        clearAppCaches()
        queryClient.clear()
        window.location.replace('/login')
      })
      .catch(error => console.error('Logout error:', error))
  }

  const logoutImpersonate = async () => {
    await stopUserImpersonate()
    window.location.replace('/login')
  }

  const renderSubmenus = (submenus, level = 1) => {
    return (
      <List disablePadding>
        {submenus.map(sub => (
          <Box key={sub.id}>
            <ListItem disablePadding sx={{ pl: level * 2 }}>
              <ListItemButton
                component={sub.url ? Link : 'div'}
                to={sub.url || undefined}
                onClick={() => {
                  if (sub.submenus?.length) toggleSubmenu(sub.id)
                }}
              >
                <ListItemIcon>{sub.icon}</ListItemIcon>
                <Tooltip title={t(sub.labelKey)}>
                  <ListItemText
                    primary={t(sub.labelKey)}
                    primaryTypographyProps={{
                      noWrap: true,
                      sx: { overflow: 'hidden', textOverflow: 'ellipsis' },
                    }}
                  />
                </Tooltip>
                {sub.submenus?.length > 0 &&
                  (openSubmenus[sub.id] ? <ExpandLess /> : <ExpandMore />)}
              </ListItemButton>
            </ListItem>
            {sub.submenus?.length > 0 &&
              openSubmenus[sub.id] &&
              renderSubmenus(sub.submenus, level + 1)}
          </Box>
        ))}
      </List>
    )
  }

  const collapseAllSubmenus = () => {
    const allClosed = Object.keys(openSubmenus).reduce((acc, key) => {
      acc[key] = false
      return acc
    }, {})
    setOpenSubmenus(allClosed)
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar position="fixed" open={open}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={() => setOpen(true)}
            edge="start"
            sx={{ marginRight: 5, ...(open && { display: 'none' }) }}
          >
            <MenuIcon />
          </IconButton>
          <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
            <Typography variant="h6" noWrap component="div">
              Core
            </Typography>
            <ButtonBase onClick={() => navigate(`/users/${user.id}`)}>
              <Typography variant="h6" noWrap component="div">
                {user?.email}
              </Typography>
            </ButtonBase>
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer
        variant="permanent"
        open={isDrawerOpen}
        onMouseEnter={() => !openLock && setOpen(true)}
        onMouseLeave={() => !openLock && setOpen(false)}
      >
        <DrawerHeader>
          <Box
            display="flex"
            gap={1}
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            <Tooltip title={t('menu.collapseAllSubmenus')}>
              <IconButton onClick={collapseAllSubmenus}>
                <UnfoldLess />
              </IconButton>
            </Tooltip>
            <Tooltip title={openLock ? t('menu.unlockMenu') : t('menu.lockMenu')}>
              <IconButton onClick={() => setOpenLock(!openLock)}>
                {openLock ? <Lock /> : <LockOpen />}
              </IconButton>
            </Tooltip>
          </Box>
        </DrawerHeader>
        <Divider />
        <List>
          {visibleItems.map(item => (
            <Box key={item.id}>
              <ListItem disablePadding sx={{ display: 'block' }}>
                <ListItemButton
                  component={item.url && !item.submenus.length ? Link : 'div'}
                  to={item.url && !item.submenus.length ? item.url : undefined}
                  onClick={() => item.submenus.length && toggleSubmenu(item.id)}
                  sx={{
                    minHeight: 48,
                    justifyContent: isDrawerOpen ? 'initial' : 'center',
                    px: 2.5,
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: isDrawerOpen ? 3 : 'auto',
                      justifyContent: 'center',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={t(item.labelKey)} sx={{ opacity: isDrawerOpen ? 1 : 0 }} />
                  {item.submenus.length > 0 &&
                    isDrawerOpen &&
                    (openSubmenus[item.id] ? <ExpandLess /> : <ExpandMore />)}
                </ListItemButton>
              </ListItem>
              {isDrawerOpen &&
                item.submenus &&
                openSubmenus[item.id] &&
                renderSubmenus(item.submenus)}
            </Box>
          ))}
        </List>
        <Divider />
        <List sx={{ marginTop: 'auto' }}>
          <ListItem disablePadding>
            <ListItemButton onClick={handleOpenLangMenu}>
              <ListItemIcon>
                <Translate />
              </ListItemIcon>
              <ListItemText
                primaryTypographyProps={{
                  noWrap: true,
                  sx: { overflow: 'hidden', textOverflow: 'ellipsis' },
                }}
                primary={t('menu.translate.title')}
              />
            </ListItemButton>
          </ListItem>

          <Menu
            anchorEl={langAnchorEl}
            open={Boolean(langAnchorEl)}
            onClose={handleCloseLangMenu}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem selected={i18n.language === 'en'} onClick={() => handleChangeLanguage('en')}>
              {t('menu.translate.english')}
            </MenuItem>
            <MenuItem selected={i18n.language === 'es'} onClick={() => handleChangeLanguage('es')}>
              {t('menu.translate.spanish')}
            </MenuItem>
          </Menu>

          <ListItem disablePadding>
            <ListItemButton onClick={user?.impersonated ? logoutImpersonate : logout}>
              <ListItemIcon>
                <Logout />
              </ListItemIcon>
              <ListItemText primary={t('menu.logout')} />
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>
      <Box component="main" sx={{ flexGrow: 1, p: 3, overflow: 'auto' }}>
        <DrawerHeader />
        <Outlet />
      </Box>
    </Box>
  )
}

export default MiniDrawer
